import React, { useState } from "react";
import * as stock from "../../../services/inventoryStockService";
import { DataTable, act, hint, linkBtn } from "../../Fin/gl/glUi";
import { EmptyState, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, formatDate, inputStyle, pageStyle, useLoad } from "../../../components/ui";
import { ItemPicker, TrackingInput, WarehouseSelect, cleanDetails, fmtAmt, fmtQty, useInvSettings } from "./invUi";

// Inventory → Transfers (stock moving between warehouses, with its batches / serials; booked when the
// two warehouses belong to different Finance locations) and Stock Takes (count sheets per warehouse:
// freeze, count, recount, review the differences, post).

const COUNT_STATUS = { COUNTING: "Counting", SUBMITTED: "Waiting for approval", POSTED: "Posted", CANCELLED: "Cancelled" };

function NewTransfer({ onHide, onDone }) {
  const [h, setH] = useState({ from_warehouse_id: null, to_warehouse_id: null, remarks: "" });
  const [lines, setLines] = useState([{ item: null, quantity: "", details: [] }]);
  const [busy, setBusy] = useState(false);
  const set = (i, patch) => setLines(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const save = async () => {
    setBusy(true);
    const res = await act(() => stock.postTransfer({ ...h, lines: lines.filter((l) => l.item).map((l) => ({ item_id: l.item.id, quantity: Number(l.quantity), details: cleanDetails(l.details) })) }));
    setBusy(false);
    if (res) {
      onDone();
      onHide();
    }
  };
  return (
    <SlidePanel show onHide={() => !busy && onHide()} title="Move stock between warehouses" width={760} footer={<><SecondaryButton onClick={onHide} disabled={busy}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={busy}>Post transfer</PrimaryButton></>}>
      <div className="d-flex flex-wrap gap-2 mb-3">
        <div style={{ flex: 1, minWidth: 220 }}>
          <label className="form-label" htmlFor="tr-from">From</label>
          <WarehouseSelect id="tr-from" value={h.from_warehouse_id} onChange={(v) => setH({ ...h, from_warehouse_id: v })} />
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <label className="form-label" htmlFor="tr-to">To</label>
          <WarehouseSelect id="tr-to" value={h.to_warehouse_id} onChange={(v) => setH({ ...h, to_warehouse_id: v })} />
        </div>
      </div>
      {lines.map((l, i) => (
        <div key={i} className="mb-2 pb-2" style={{ borderBottom: "1px solid #F3F4F6" }}>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <div style={{ flex: 1, minWidth: 280 }}>
              <ItemPicker id={`tr-it-${i}`} value={l.item ? l.item.id : null} warehouseId={h.from_warehouse_id} onChange={(item) => set(i, { item, details: [] })} />
            </div>
            <input type="number" min={0} step="any" className="form-control" style={{ ...inputStyle, width: 110 }} placeholder="Qty" aria-label={`Quantity line ${i + 1}`} value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
            {lines.length > 1 ? (
              <button type="button" style={linkBtn} onClick={() => setLines(lines.filter((_, k) => k !== i))}>
                Remove
              </button>
            ) : null}
          </div>
          <TrackingInput item={l.item} qty={l.quantity} value={l.details} onChange={(d) => set(i, { details: d })} mode="out" itemId={l.item && l.item.id} warehouseId={h.from_warehouse_id} />
        </div>
      ))}
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setLines([...lines, { item: null, quantity: "", details: [] }])}>
        Add item
      </button>
      <input className="form-control mt-3" style={inputStyle} placeholder="Remarks" aria-label="Remarks" value={h.remarks} onChange={(e) => setH({ ...h, remarks: e.target.value })} />
      <div style={{ ...hint, marginTop: 6 }}>Reserved stock cannot move. Batches leave earliest expiry first unless you choose them.</div>
    </SlidePanel>
  );
}

function TransferView({ id, onHide }) {
  const { data: t, loading, error, reload } = useLoad(() => stock.getTransfer(id), [id]);
  return (
    <SlidePanel show onHide={onHide} title={t ? `Transfer ${t.transfer_number}` : "Transfer"} width={720}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {t ? (
          <DataTable
            rows={t.lines}
            columns={[
              { label: "Item", render: (l) => `${l.sku} ${l.item_name}` },
              { label: "Qty", num: true, render: (l) => fmtQty(l.quantity) },
              { label: "Batches / serials", render: (l) => (l.tracking || []).map((d) => d.serial_no || `${d.lot_no} ×${fmtQty(Math.abs(d.qty))}`).join(", ") },
              { label: "Cost", num: true, render: (l) => fmtAmt(Number(l.quantity) * Number(l.unit_cost)) },
            ]}
          />
        ) : null}
      </LoadState>
    </SlidePanel>
  );
}

export function Transfers() {
  const { data: s } = useInvSettings();
  const { data, loading, error, reload } = useLoad(() => stock.listTransfers(), []);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(null);
  return (
    <div style={pageStyle}>
      <PageHeader title="Transfers" subtitle="Stock moved between warehouses" actions={s && s.can.purchase ? <PrimaryButton onClick={() => setCreating(true)}>New transfer</PrimaryButton> : null} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No transfers yet" />}
          columns={[
            { label: "Transfer", render: (r) => <b>{r.transfer_number}</b> },
            { label: "Date", render: (r) => formatDate(r.transfer_date) },
            { label: "From → to", render: (r) => `${r.from_code} → ${r.to_code}` },
            { label: "Lines", num: true, render: (r) => r.line_count },
            { label: "Booked", render: (r) => (r.accounting_event_id ? "Yes" : "") },
            { label: "Remarks", render: (r) => r.remarks || "" },
          ]}
        />
      </LoadState>
      {creating ? <NewTransfer onHide={() => setCreating(false)} onDone={reload} /> : null}
      {open ? <TransferView id={open} onHide={() => setOpen(null)} /> : null}
    </div>
  );
}

// ---------- stock takes ----------

function StartCount({ onHide, onDone }) {
  const classes = useLoad(() => stock.listItemClasses(), []);
  const [h, setH] = useState({ warehouse_id: null, bin_prefix: "", item_class_id: "", abc: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const res = await act(() => stock.startCount({ warehouse_id: h.warehouse_id, bin_prefix: h.bin_prefix || undefined, item_class_id: h.item_class_id || undefined, abc: h.abc || undefined }));
    setBusy(false);
    if (res) {
      onDone(res.data.id);
      onHide();
    }
  };
  return (
    <SlidePanel show onHide={() => !busy && onHide()} title="Start a stock take" width={520} footer={<><SecondaryButton onClick={onHide} disabled={busy}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={busy || !h.warehouse_id}>Make the count sheet</PrimaryButton></>}>
      <label className="form-label" htmlFor="sc-wh">Warehouse</label>
      <WarehouseSelect id="sc-wh" value={h.warehouse_id} onChange={(v) => setH({ ...h, warehouse_id: v })} />
      <div style={{ ...hint, margin: "10px 0 4px" }}>Count everything, or one part of it (a cycle count):</div>
      <input className="form-control mb-2" style={inputStyle} placeholder="Bins starting with (e.g. A-01)" aria-label="Bin prefix" value={h.bin_prefix} onChange={(e) => setH({ ...h, bin_prefix: e.target.value })} />
      <select className="form-select mb-2" aria-label="Item class" value={h.item_class_id} onChange={(e) => setH({ ...h, item_class_id: e.target.value })}>
        <option value="">All classes</option>
        {(classes.data || []).map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <select className="form-select" aria-label="ABC class" value={h.abc} onChange={(e) => setH({ ...h, abc: e.target.value })}>
        <option value="">All items</option>
        <option value="A">A items (fast / high value: count often)</option>
        <option value="B">B items</option>
        <option value="C">C items</option>
      </select>
      <div style={{ ...hint, marginTop: 8 }}>The sheet freezes what the system holds now. Stock can still move while you count; the review shows the snapshot so you can recount.</div>
    </SlidePanel>
  );
}

function CountSheet({ id, s, onHide, onChanged }) {
  const { data: c, loading, error, reload } = useLoad(() => stock.getCount(id), [id]);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const open = c && c.status === "COUNTING";
  const save = async (submit) => {
    setBusy(true);
    const entries = Object.entries(edits).map(([lineId, e]) => ({ line_id: Number(lineId), ...e }));
    const res = await act(() => stock.enterCounts(id, { entries, submit }));
    setBusy(false);
    if (res) {
      setEdits({});
      reload();
      onChanged();
    }
  };
  const action = async (a) => {
    if (await act(() => stock.countAction(id, a))) {
      reload();
      onChanged();
    }
  };
  const field = (l, key) => (edits[l.id] && edits[l.id][key] !== undefined ? edits[l.id][key] : l[key] === null || l[key] === undefined ? "" : l[key]);
  const put = (l, key, v) => setEdits({ ...edits, [l.id]: { ...(edits[l.id] || {}), [key]: v } });
  return (
    <SlidePanel show onHide={onHide} title={c ? `Stock take ${c.count_number} · ${c.warehouse_code}` : "Stock take"} width={1040}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {c ? (
          <>
            <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
              <span style={hint}>
                {COUNT_STATUS[c.status]} · {c.totals.counted} of {c.totals.lines} counted · {c.totals.with_difference} with a difference · {fmtAmt(c.totals.variance_value)}
              </span>
              {open ? <SecondaryButton disabled={busy} onClick={() => save(false)}>Save counts</SecondaryButton> : null}
              {open ? <PrimaryButton disabled={busy} onClick={() => save(true)}>Submit for approval</PrimaryButton> : null}
              {c.status === "SUBMITTED" && s.can.manage ? <PrimaryButton onClick={() => action("post")}>Post the differences</PrimaryButton> : null}
              {c.status === "SUBMITTED" && s.can.manage ? <SecondaryButton onClick={() => action("recount")}>Send back for a recount</SecondaryButton> : null}
              {["COUNTING", "SUBMITTED"].includes(c.status) && s.can.manage ? <SecondaryButton onClick={() => action("cancel")}>Cancel the count</SecondaryButton> : null}
            </div>
            <DataTable
              rows={c.lines}
              columns={[
                { label: "Bin", render: (l) => l.bin_location || "" },
                { label: "Item", render: (l) => `${l.sku} ${l.item_name}` },
                { label: "Batch", render: (l) => (l.lot_no ? `${l.lot_no}${l.expiry_date ? ` · ${formatDate(l.expiry_date)}` : ""}` : "") },
                { label: "System", num: true, render: (l) => fmtQty(l.system_qty) },
                {
                  label: "Counted",
                  render: (l) =>
                    !open ? (
                      l.counted_qty === null ? "" : fmtQty(l.counted_qty)
                    ) : l.tracking === "SERIAL" ? (
                      <textarea className="form-control form-control-sm" rows={1} style={{ minWidth: 180 }} placeholder="Serials found" aria-label={`Serials found ${l.sku}`} value={Array.isArray(field(l, "serials_found")) ? field(l, "serials_found").join(" ") : field(l, "serials_found")} onChange={(e) => put(l, "serials_found", e.target.value.split(/[\s,]+/).filter(Boolean))} />
                    ) : (
                      <input type="number" min={0} step="any" className="form-control form-control-sm" style={{ width: 100 }} aria-label={`Counted ${l.sku}`} value={field(l, "counted_qty")} onChange={(e) => put(l, "counted_qty", e.target.value)} />
                    ),
                },
                {
                  label: "Recount",
                  render: (l) =>
                    open && l.counted_qty !== null && l.tracking !== "SERIAL" ? (
                      <input type="number" min={0} step="any" className="form-control form-control-sm" style={{ width: 100 }} aria-label={`Recount ${l.sku}`} value={field(l, "recount_qty")} onChange={(e) => put(l, "recount_qty", e.target.value)} />
                    ) : l.recount_qty === null ? (
                      ""
                    ) : (
                      fmtQty(l.recount_qty)
                    ),
                },
                { label: "Difference", num: true, render: (l) => (l.variance ? <span style={{ color: l.variance < 0 ? "#B91C1C" : "#065F46", fontWeight: 600 }}>{l.variance > 0 ? "+" : ""}{fmtQty(l.variance)}</span> : "") },
                { label: "Value", num: true, render: (l) => (l.variance_value ? fmtAmt(l.variance_value) : "") },
              ]}
            />
          </>
        ) : null}
      </LoadState>
    </SlidePanel>
  );
}

export function StockTakes() {
  const { data: s } = useInvSettings();
  const { data, loading, error, reload } = useLoad(() => stock.listCounts(), []);
  const [starting, setStarting] = useState(false);
  const [open, setOpen] = useState(null);
  return (
    <div style={pageStyle}>
      <PageHeader title="Stock Takes" subtitle="Count sheets per warehouse, reviewed and posted" actions={s && s.can.count ? <PrimaryButton onClick={() => setStarting(true)}>Start a stock take</PrimaryButton> : null} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No stock takes yet" />}
          columns={[
            { label: "Count", render: (r) => <b>{r.count_number}</b> },
            { label: "Date", render: (r) => formatDate(r.count_date) },
            { label: "Warehouse", render: (r) => r.warehouse_code },
            { label: "Lines", num: true, render: (r) => r.line_count },
            { label: "Status", render: (r) => COUNT_STATUS[r.status] || r.status },
          ]}
        />
      </LoadState>
      {starting ? <StartCount onHide={() => setStarting(false)} onDone={(id) => { reload(); setOpen(id); }} /> : null}
      {open && s ? <CountSheet id={open} s={s} onHide={() => setOpen(null)} onChanged={reload} /> : null}
    </div>
  );
}
